import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './HomePage.css';

const services = [
  { icon: '🔍', title: 'Product Sourcing', desc: 'We find any product from Korean online and offline markets — fashion, beauty, electronics, collectibles, and more.' },
  { icon: '💬', title: 'Price Negotiation', desc: 'Our team negotiates with local vendors to get you the best possible price before purchase.' },
  { icon: '📦', title: 'Quality Inspection', desc: 'Every item is carefully inspected before being packed and dispatched to ensure your satisfaction.' },
  { icon: '✈️', title: 'Worldwide Shipping', desc: 'We ship to over 150 countries via EMS and FedEx with full tracking from Seoul to your door.' },
];

const steps = [
  { num: '01', icon: '📋', title: 'Submit Your Request', desc: 'Fill in our order form with product details, photos, or links. Include your shipping address so we can calculate the total.' },
  { num: '02', icon: '💬', title: 'Receive a Quote', desc: 'We review your request and email you a detailed quote — product cost, shipping fee, and service charge. No hidden fees.' },
  { num: '03', icon: '💳', title: 'Pay via PayPal Invoice', desc: "Accept the quote and we send a secure PayPal invoice to your email. Pay safely using PayPal, card, or bank account.", paypal: true },
  { num: '04', icon: '🚀', title: 'Shipped to Your Door', desc: "We purchase, inspect, and dispatch via EMS or FedEx. You'll receive a tracking number the moment it ships." },
];

const shippingOptions = [
  { carrier: 'EMS', flag: '📮', time: '5–10 business days', desc: 'Korea Post EMS — reliable tracked postal service available to 150+ countries at affordable rates.', best: 'Best for smaller packages & budget shipping' },
  { carrier: 'FedEx', flag: '📦', time: '3–5 business days', desc: 'FedEx International Priority — fast, fully insured, door-to-door delivery worldwide.', best: 'Best for urgent or high-value orders' },
];

/* PayPal wordmark using styled text — always visible */
const PayPalWordmark = ({ size = 'md', dark = false }) => (
  <span className={`paypal-wordmark paypal-wordmark-${size} ${dark ? 'paypal-wordmark-dark' : ''}`}>
    <span className="pp-pay">Pay</span><span className="pp-pal">Pal</span>
  </span>
);

export default function HomePage() {
  const heroRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) heroRef.current.style.setProperty('--parallax', `${window.scrollY * 0.4}px`);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="home">
      {/* Navbar */}
      <nav className="navbar">
        <div className="nav-inner">
          <div className="nav-logo">
            <div className="logo-img-wrap">
              <img src="/buykorea-logo.png" alt="BuyKorea Logo" style={{ height: '32px', objectFit: 'contain' }} />
            </div>
          </div>
          <div className="nav-links">
            <a href="#services">Services</a>
            <a href="#how-it-works">How It Works</a>
            <a href="#shipping">Shipping</a>
            <a href="#why-us">Why Us</a>
            <Link to="/order" className="btn btn-primary btn-sm nav-cta">Place Order</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero" ref={heroRef}>
        <div className="hero-bg">
          <div className="hero-grain"></div>
          <div className="orb orb-1"></div>
          <div className="orb orb-2"></div>
          <div className="orb orb-3"></div>
          <div className="grid-lines"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge">
            <span className="badge-dot"></span>
            <span>Korean Shopping Service</span>
            <span className="badge-kr">한국 직구 대행</span>
          </div>
          <h1 className="hero-title">
            <span className="title-line">Your Gateway</span>
            <span className="title-line italic">to Korea</span>
          </h1>
          <p className="hero-sub">
            We source, inspect, and ship any Korean product directly to your doorstep —
            fashion, beauty, electronics, and everything in between.
          </p>
          <div className="hero-actions">
            <Link to="/order" className="btn btn-primary btn-lg hero-btn">
              <span>Start Your Order</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </Link>
            <a href="#how-it-works" className="btn btn-outline btn-lg hero-btn-outline">How It Works</a>
          </div>

          {/* Trust bar — white background for visibility */}
          <div className="hero-trust">
            <span className="trust-label">Secure payment via</span>
            <div className="trust-paypal-pill">
              <PayPalWordmark size="sm" dark />
            </div>
            <span className="trust-divider">·</span>
            <span className="trust-item">🔒 Buyer Protection</span>
            <span className="trust-divider">·</span>
            <span className="trust-item">✈️ EMS &amp; FedEx</span>
          </div>

          <div className="hero-stats">
            <div className="stat"><span className="stat-num">5K+</span><span className="stat-label">Orders Fulfilled</span></div>
            <div className="stat-div"></div>
            <div className="stat"><span className="stat-num">80+</span><span className="stat-label">Countries Served</span></div>
            <div className="stat-div"></div>
            <div className="stat"><span className="stat-num">99%</span><span className="stat-label">Satisfaction Rate</span></div>
          </div>
        </div>
        <div className="hero-scroll">
          <div className="scroll-indicator"><div className="scroll-dot"></div></div>
          <span>Scroll</span>
        </div>
      </section>

      {/* Services */}
      <section className="services-section" id="services">
        <div className="container">
          <div className="section-header">
            <p className="section-tag">What We Do</p>
            <h2 className="section-title">Full-Service Korean Shopping</h2>
            <p className="section-sub">From finding the item to delivering it worldwide — we handle everything.</p>
          </div>
          <div className="services-grid">
            {services.map((s, i) => (
              <div className="service-card" key={i} style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="service-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="hiw-section" id="how-it-works">
        <div className="container">
          <div className="section-header light">
            <p className="section-tag">Simple Process</p>
            <h2 className="section-title">How It Works</h2>
            <p className="section-sub">Four easy steps — from request to delivery at your door.</p>
          </div>
          <div className="steps-grid">
            {steps.map((s, i) => (
              <div className="step-card" key={i}>
                <div className="step-num">{s.num}</div>
                <div className="step-icon-big">{s.icon}</div>
                <div className="step-connector"></div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                {s.paypal && (
                  <div className="step-paypal-tag">
                    <PayPalWordmark size="xs" dark />
                    <span>Invoice sent to your email</span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="hiw-cta">
            <Link to="/order" className="btn btn-primary btn-lg">Place Your Order Now</Link>
          </div>
        </div>
      </section>

      {/* PayPal Trust Section */}
      <section className="paypal-section">
        <div className="container">
          <div className="paypal-inner">
            <div className="paypal-left">
              <p className="section-tag" style={{ color: 'var(--gold-dark)' }}>Safe &amp; Secure Payments</p>
              <h2 className="section-title">Pay with Confidence</h2>
              <p className="paypal-desc">
                We invoice you directly via PayPal — the world's most trusted online payment platform.
                Every order is protected by PayPal Buyer Protection.
              </p>
              <ul className="paypal-features">
                {[
                  { title: 'PayPal Invoice to your email', sub: 'We send a detailed invoice — pay only after you approve the quote' },
                  { title: 'No PayPal account required', sub: 'Pay with credit card, debit card, or PayPal balance' },
                  { title: 'Buyer Protection included', sub: "Get a full refund if your item doesn't arrive or isn't as described" },
                  { title: 'No payment until you approve', sub: 'Review the full price breakdown before any charge is made' },
                ].map((f, i) => (
                  <li key={i}>
                    <span className="paypal-check">✓</span>
                    <div><strong>{f.title}</strong><span>{f.sub}</span></div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="paypal-right">
              <div className="paypal-card">
                <div className="paypal-card-header">
                  <PayPalWordmark size="lg" dark />
                  <span className="paypal-card-tag">Invoice</span>
                </div>
                <div className="paypal-mock-invoice">
                  <div className="invoice-row"><span>Product cost</span><span>Quoted price</span></div>
                  <div className="invoice-row"><span>Shipping (EMS / FedEx)</span><span>By destination</span></div>
                  <div className="invoice-row"><span>Service fee</span><span>Included</span></div>
                  <div className="invoice-total"><span>Total</span><span>Sent to your email ✉️</span></div>
                </div>
                <div className="paypal-protection">
                  <span>🔒</span>
                  <span>Protected by PayPal Buyer Protection</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shipping Section */}
      <section className="shipping-section" id="shipping">
        <div className="container">
          <div className="section-header">
            <p className="section-tag">Worldwide Delivery</p>
            <h2 className="section-title">Shipping from Seoul</h2>
            <p className="section-sub">We ship via EMS or FedEx — choose based on speed and budget.</p>
          </div>
          <div className="shipping-grid">
            {shippingOptions.map((opt, i) => (
              <div className="shipping-card" key={i}>
                <div className="shipping-carrier">{opt.flag} {opt.carrier}</div>
                <div className="shipping-time">{opt.time}</div>
                <p className="shipping-desc">{opt.desc}</p>
                <div className="shipping-best">{opt.best}</div>
              </div>
            ))}
          </div>
          <div className="shipping-note">
            <span>📍</span>
            <p>All orders ship from Seoul, South Korea. Tracking number provided after dispatch. Import duties may apply depending on your country.</p>
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="why-section" id="why-us">
        <div className="container">
          <div className="why-inner">
            <div className="why-left">
              <p className="section-tag">Why BUY KOREA</p>
              <h2 className="section-title">Korea at<br/>Your Fingertips</h2>
              <p className="why-desc">
                We're based in Seoul with years of experience navigating Korean retail —
                online shops, department stores, markets, and exclusive drops.
                If it exists in Korea, we can get it for you.
              </p>
              <ul className="why-list">
                {['Fast response within 24 hours','No hidden fees — full price breakdown','Secure PayPal invoice payment','EMS & FedEx tracking worldwide','Korean & English support'].map((item, i) => (
                  <li key={i}><span className="check">✓</span>{item}</li>
                ))}
              </ul>
              <Link to="/order" className="btn btn-primary" style={{ marginTop: '28px', display: 'inline-flex' }}>Get Started Today</Link>
            </div>
            <div className="why-right">
              <div className="why-card-stack">
                <div className="why-card card-1"><div className="card-icon">🛍️</div><div><div className="card-title">Dongdaemun Fashion</div><div className="card-sub">Latest K-Fashion Trends</div></div></div>
                <div className="why-card card-2"><div className="card-icon">💄</div><div><div className="card-title">K-Beauty Products</div><div className="card-sub">Authentic Skincare &amp; Makeup</div></div></div>
                <div className="why-card card-3"><div className="card-icon">📱</div><div><div className="card-title">Korean Electronics</div><div className="card-sub">Latest Gadgets &amp; Devices</div></div></div>
                <div className="why-deco-circle"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="cta-banner">
        <div className="container">
          <div className="cta-inner">
            <div>
              <h2>Ready to Order from Korea?</h2>
              <p>Submit your request — get a quote within 24 hours, pay safely via PayPal.</p>
            </div>
            <Link to="/order" className="btn btn-primary btn-lg">
              Place an Order
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-inner">
            <div className="footer-brand">
              <div className="logo-img-wrap logo-img-wrap-sm">
                <img src="/buykorea-logo.png" alt="BuyKorea Logo" style={{ height: '26px', objectFit: 'contain' }} />
              </div>
              <p>Korean Shopping Service — 한국 직구 대행</p>
              <p className="footer-domain">buykorea.app</p>
              <div className="footer-paypal-row">
                <span>Payments by</span>
                <PayPalWordmark size="xs" />
              </div>
            </div>
            <div className="footer-links">
              <Link to="/order">Place Order</Link>
              <a href="#services">Services</a>
              <a href="#how-it-works">How It Works</a>
              <a href="#shipping">Shipping</a>
              <Link to="/admin/login" style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Admin</Link>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2025 BuyKorea. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}